import React, { useMemo, useState } from 'react';
import { Chip } from '../ModuleFrame';
import { apiPost } from '../../utils/backendApi';
import { ACTIVE, ActiveBar, Composer, EmptyDetail, ProposalCard, RunCard, RunClock, Shot, UsageLine, ago, fmtSec } from './shared';
import LiveBrowser from './LiveBrowser';

// Research phase: composer + run list | reader (Brief · Sources · Proposals · Ideas).
// Two kinds share the list: `research` (provider-side web search, background)
// and `browse` (a real browser you can watch — [[studio-journey]]); the
// composer's mode switch picks which one the brief starts.
export default function Research({ org, app, agents, runs, info, sel, setSel, canEdit, canApply, busy, act, onTab, onPhase }) {
  const mine = runs.filter((r) => r.kind === 'research' || r.kind === 'browse');
  const run = mine.find((r) => r.id === sel) || null;
  const [seg, setSeg] = useState('brief');
  const [mode, setMode] = useState('browse');
  const eta = info?.eta || {};
  const start = (brief) => act('start', () => apiPost(`/lab/${org.id}/runs`, { appId: app.id, kind: mode, brief }).then((out) => setSel(out.runId)));
  return (
    <>
      <div className="lab-list" data-nova-note={`Research phase: ${mine.length} research/browse runs${run ? `; run ${run.id} (${run.kind}, ${run.status}) is open on the ${seg} segment` : ''}, new briefs start in ${mode} mode. NoVA: research_topic (brief → run), read findings, turn a finding into an idea.`}>
        <Composer placeholder={mode === 'browse' ? 'e.g. Who are the main help-desk apps for small IT teams, what do they charge, and where do their users complain?' : 'e.g. What do small IT teams expect from a help-desk app in 2026 — SLAs, self-service, integrations, pricing?'} buttonLabel={mode === 'browse' ? 'Browse live' : 'Run research'} canEdit={canEdit} busy={busy === 'start'} onStart={start}>
          <div className="lab-seg" role="radiogroup" aria-label="Research mode">
            <button type="button" role="radio" aria-checked={mode === 'browse'} className={`lab-seg-btn ${mode === 'browse' ? 'is-on' : ''}`} onClick={() => setMode('browse')} title="Opens a real browser you can watch; visits sites and reads them. Counts as browser minutes.">Watch it browse</button>
            <button type="button" role="radio" aria-checked={mode === 'research'} className={`lab-seg-btn ${mode === 'research' ? 'is-on' : ''}`} onClick={() => setMode('research')} title="Server-side web search; comes back with a cited brief and proposals. Counts as a research run.">Search in the background</button>
          </div>
        </Composer>
        {!mine.length && <p className="mf-muted lab-list-empty">No research yet.</p>}
        {mine.map((r) => <RunCard key={r.id} run={r} selected={r.id === sel} onClick={() => setSel(r.id)} extra={r.kind === 'browse' ? <span className="mf-muted lab-card-sub">{ACTIVE.has(r.status) ? (r.live?.caption || 'browsing live') : `${(r.findings?.visits || []).filter((v) => !v.failed).length} sites visited`}</span> : ACTIVE.has(r.status) && r.live?.total ? <span className="mf-muted lab-card-sub">{r.live.done || 0}/{r.live.total} searches · {r.live.sources || 0} sources so far</span> : r.findings?.sources?.length ? <span className="mf-muted lab-card-sub">{r.findings.sources.length} sources · {(r.proposals || []).length} proposals</span> : null} />)}
      </div>
      <div className="lab-detail">
        {!run && <EmptyDetail phase="research"><p className="cs-help">Describe what to find out on the left. “Watch it browse” opens a real browser in the Lab and shows you every page as it reads it; “Search in the background” searches server-side and comes back with a brief, numbered sources, design proposals and ideas.</p></EmptyDetail>}
        {run && run.kind === 'browse' && <BrowseReader org={org} run={run} etaSec={eta.browse} canEdit={canEdit} busy={busy} act={act} onPhase={onPhase} />}
        {run && run.kind === 'research' && <Reader org={org} app={app} agents={agents} run={run} etaSec={eta.research} seg={seg} setSeg={setSeg} canEdit={canEdit} canApply={canApply} busy={busy} act={act} onTab={onTab} onPhase={onPhase} />}
      </div>
    </>
  );
}

// The parallel search agents of a research run, live: one card per query
// (queued → searching → done/failed) with the sources as they land. Reads
// labRun.live.searches, which each chunk task patches as it works.
function SearchBoard({ run: r }) {
  const cards = Object.entries(r.live?.searches || {}).sort((a, b) => Number(a[0]) - Number(b[0])).map(([i, c]) => ({ i, ...c }));
  if (!cards.length) return null;
  const host = (u) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return u; } };
  return (
    <div className="lab-swarm" aria-live="polite">
      {cards.map((c) => (
        <div key={c.i} className={`lab-agent is-${c.status || 'queued'}`}>
          <div className="lab-agent-head">
            <span className="lab-agent-dot" aria-hidden="true" />
            <span className="lab-agent-name">Agent {Number(c.i) + 1}</span>
            <span className="mf-muted lab-agent-state">{c.status === 'searching' ? 'searching…' : c.status === 'done' ? `${c.count ?? (c.sources || []).length} sources · ${fmtSec((c.ms || 0) / 1000)}` : c.status === 'failed' ? 'failed' : 'waiting'}</span>
          </div>
          <div className="lab-agent-q">{c.q}</div>
          {c.error && <div className="cs-form-error cb-small" style={{ margin: 0 }}>{c.error}</div>}
          {(c.sources || []).length > 0 && (
            <ul className="lab-agent-src">
              {c.sources.slice(0, 4).map((s) => (
                <li key={s.url}><img className="lab-favicon" alt="" src={`https://www.google.com/s2/favicons?sz=32&domain=${encodeURIComponent(host(s.url))}`} loading="lazy" /><a href={s.url} target="_blank" rel="noreferrer">{s.title || host(s.url)}</a></li>
              ))}
              {c.sources.length > 4 && <li className="mf-muted">+{c.sources.length - 4} more</li>}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

function BrowseReader({ org, run: r, etaSec, canEdit, busy, act, onPhase }) {
  const f = r.findings || {};
  const visits = (f.visits || []).filter((v) => !v.failed);
  const active = ACTIVE.has(r.status);
  const [cur, setCur] = useState(null);
  const v = visits.find((x) => x.url === cur) || null;
  return (
    <>
      <header className="lab-detail-head">
        <div>
          <h3>{r.brief}</h3>
          <span className="mf-muted">{active ? (r.stage || 'browsing…') : `${r.status} · ${ago(r.finishedAt || r.updatedAt)}`}{visits.length ? ` · ${visits.length} sites` : ''}{f.queries?.length ? ` · searched: ${f.queries.join(' · ')}` : ''}</span>
          {active && <> · <RunClock run={r} etaSec={etaSec} /></>}
        </div>
        {r.status === 'done' && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onPhase?.('concept')}>Draft the concept →</button>}
      </header>
      {r.error && <p className="cs-form-error" style={{ margin: 0 }}>{r.error}</p>}
      {(active || !visits.length) && <LiveBrowser org={org} run={r} canEdit={canEdit} busy={busy} act={act} />}
      {f.summary && <div className="lab-md">{f.summary}</div>}
      {visits.length > 0 && (
        <div className="lab-split">
          <ol className="lab-sources-list">
            {visits.map((x) => {
              let host = x.host || ''; try { host = new URL(x.url).hostname.replace(/^www\./, ''); } catch { /* ignore */ }
              return (
                <li key={x.url} className={x.url === cur ? 'is-cur' : ''}>
                  <img className="lab-favicon" alt="" src={`https://www.google.com/s2/favicons?sz=32&domain=${encodeURIComponent(host)}`} loading="lazy" />
                  <span className="lab-src-text"><button type="button" className="cs-btn-link" onClick={() => setCur(x.url === cur ? null : x.url)}>{x.title || x.url}</button><span className="mf-muted">{host}{x.facts?.pricing ? ` · ${x.facts.pricing}` : ''}{x.facts?.relevance ? ` · ${x.facts.relevance} relevance` : ''}</span></span>
                </li>
              );
            })}
          </ol>
          {v && (
            <div className="lab-film">
              {v.keyframeKey && <Shot org={org} runId={r.id} k={v.keyframeKey} large auto alt={v.title} />}
              {v.caption && <p className="lab-live-cap">{v.caption}</p>}
              {v.facts && (
                <dl className="lab-kv">
                  {v.facts.what && <><dt>What</dt><dd>{v.facts.what}</dd></>}
                  {v.facts.positioning && <><dt>Positioning</dt><dd>{v.facts.positioning}</dd></>}
                  {v.facts.pricing && <><dt>Pricing</dt><dd>{v.facts.pricing}</dd></>}
                  {v.facts.strengths?.length ? <><dt>Strong</dt><dd>{v.facts.strengths.join(' · ')}</dd></> : null}
                  {v.facts.weaknesses?.length ? <><dt>Weak</dt><dd>{v.facts.weaknesses.join(' · ')}</dd></> : null}
                </dl>
              )}
              <a href={v.url} target="_blank" rel="noreferrer" className="mf-muted cb-small">{v.url}</a>
            </div>
          )}
        </div>
      )}
      <UsageLine run={r} />
    </>
  );
}

function Reader({ org, app, agents, run: r, etaSec, seg, setSeg, canEdit, canApply, busy, act, onTab, onPhase }) {
  const f = r.findings || {};
  const proposals = (r.proposals || []).filter((p) => p.kind !== 'idea');
  const ideas = (r.proposals || []).filter((p) => p.kind === 'idea');
  const sources = f.sources || [];
  const segs = [['brief', 'Brief'], ['sources', `Sources ${sources.length}`], ['proposals', `Proposals ${proposals.length}`], ['ideas', `Ideas ${ideas.length}`]];
  // [n] citations → links that scroll the source into view.
  const md = useMemo(() => {
    const parts = String(f.markdown || '').split(/(\[\d+\])/g);
    return parts.map((t, i) => {
      const m = /^\[(\d+)\]$/.exec(t);
      if (!m) return t;
      const n = Number(m[1]);
      return <a key={i} href={`#src-${r.id}-${n}`} className="lab-cite" onClick={(e) => { e.preventDefault(); setSeg('sources'); setTimeout(() => document.getElementById(`src-${r.id}-${n}`)?.scrollIntoView({ block: 'center' }), 0); }}>[{n}]</a>;
    });
  }, [f.markdown, r.id, setSeg]);
  return (
    <>
      <header className="lab-detail-head">
        <div>
          <h3>{r.brief}</h3>
          <span className="mf-muted">{ACTIVE.has(r.status) ? r.stage : `${r.status} · ${ago(r.finishedAt || r.updatedAt)}`}{sources.length ? ` · ${sources.length} sources` : ''}{r.usage?.tokens ? ` · ${Math.round(r.usage.tokens / 1000)}K tok` : ''}</span>
        </div>
        {f.docId && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onTab?.('knowledge')}>Open in Knowledge</button>}
      </header>
      <ActiveBar org={org} run={r} canEdit={canEdit} busy={busy} act={act} etaSec={etaSec} />
      {ACTIVE.has(r.status) && <SearchBoard run={r} />}
      {r.error && <p className="cs-form-error" style={{ margin: 0 }}>{r.error}</p>}
      {r.status === 'done' && (
        <>
          <div className="lab-seg" role="tablist">
            {segs.map(([id, label]) => <button key={id} type="button" role="tab" aria-selected={seg === id} className={`lab-seg-btn ${seg === id ? 'is-on' : ''}`} onClick={() => setSeg(id)}>{label}</button>)}
          </div>
          {seg === 'brief' && <div className="lab-md lab-md-tall">{md}</div>}
          {seg === 'sources' && (
            <ol className="lab-sources-list">
              {sources.map((s, i) => {
                let host = ''; try { host = new URL(s.url).hostname.replace(/^www\./, ''); } catch { /* ignore */ }
                return (
                  <li key={s.url} id={`src-${r.id}-${i + 1}`}>
                    <img className="lab-favicon" alt="" src={`https://www.google.com/s2/favicons?sz=32&domain=${encodeURIComponent(host)}`} loading="lazy" />
                    <span className="lab-src-text"><a href={s.url} target="_blank" rel="noreferrer">{s.title || s.url}</a><span className="mf-muted">{host}</span></span>
                  </li>
                );
              })}
              {!sources.length && <li className="mf-muted">No sources were returned.</li>}
            </ol>
          )}
          {seg === 'proposals' && (
            <div className="lab-proposals">
              {!proposals.length && <p className="mf-muted" style={{ margin: 0 }}>No design proposals in this run.</p>}
              {['app', 'agent', 'knowledge', 'testCase', 'story'].map((k) => {
                const list = proposals.filter((p) => p.kind === k);
                return list.length ? <React.Fragment key={k}>{list.map((p) => <ProposalCard key={p.id} org={org} run={r} p={p} canApply={canApply} busy={busy} act={act} app={app} agents={agents} onApplied={(pp) => { if (pp.kind === 'story') onPhase?.('stories'); }} />)}</React.Fragment> : null;
              })}
            </div>
          )}
          {seg === 'ideas' && (
            <div className="lab-proposals">
              {!ideas.length && <p className="mf-muted" style={{ margin: 0 }}>This run proposed no ideas.</p>}
              {ideas.map((p) => (
                <div key={p.id} className={`lab-proposal lab-idea ${p.applied ? 'is-applied' : ''}`}>
                  <div className="lab-proposal-head">
                    <Chip tone="info">idea</Chip><strong>{p.title}</strong><span style={{ flex: 1 }} />
                    {p.applied ? <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onPhase?.('ideas', p.applied.artifactId)}>On the board →</button> : canApply ? (
                      <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy === `apply-${r.id}-${p.id}`} onClick={() => act(`apply-${r.id}-${p.id}`, () => apiPost(`/lab/${org.id}/runs/${r.id}/apply`, { proposalId: p.id }))}>{busy === `apply-${r.id}-${p.id}` ? 'Adding…' : 'Add to ideas'}</button>
                    ) : null}
                  </div>
                  <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{p.patch?.body}</p>
                  {p.patch?.value && <p className="mf-muted" style={{ margin: '4px 0 0' }}>Value: {p.patch.value}</p>}
                  {p.rationale && <p className="mf-muted" style={{ margin: '4px 0 0' }}>{p.rationale}</p>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <UsageLine run={r} />
    </>
  );
}
